import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission, type UnpairedReason } from '@construction/shared';
import { attendanceApi, type AttendancePerson, type Punch } from '@/api/endpoints';
import {
  Alert,
  Badge,
  Button,
  DataTable,
  Drawer,
  EmptyState,
  FilterBar,
  ListGroup,
  ListRow,
  Spinner,
  TableRow,
} from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { splitHours } from '@/lib/format';
import { usePermissions } from '@/lib/permissions';
import { siteDayRange, widenRange } from '@/lib/site-time';
import { cn } from '@/lib/cn';
import { buildDays, type DayRow } from './day-sessions';
import { invalidateAttendance } from './invalidate';
import { PunchItem, PunchRow } from './PunchRow';
import { SessionLine } from './SessionLine';

/** 区间不超过这么多天就画「人 × 天」的周表，再长一行放不下，换成按人汇总 */
const MATRIX_MAX_DAYS = 16;

/**
 * 工时表（CR-025 的「按人汇总」，行业叫 Timesheets；CR-082 重做）。
 *
 * 客户原话：「筛选后 按人为一列，显示出他的上班天数，总工时。点击这个人后可以展开细节。」
 * - **区间 ≤ 16 天是「人 × 天」的周表**（CR-082，照 ADP / UKG 的 Timecard grid）：一格是那天的工时，
 *   有没处理的异常标红、有待复核的卡标橙，右边合计、底行每天合计。双周结算一眼看完。
 * - 区间更长就是一行一个人：天数、总工时、待办。
 * - **点一个人从右侧（手机从底部）滑出他每天的上下班**：一天一行、每段上下班连成一条线（`day-sessions.ts`）。
 *
 * **数字全来自服务端**（`/attendance/summary`，每人每天的分钟数也是），不是拿列表接口的结果在前端加总 ——
 * 那个接口有 500 条截断，二十个人两周就超了，加出来的总工时会静默少算。
 */
export function PersonList({
  siteId,
  timezone,
  from,
  to,
  toolbar,
}: {
  siteId: number;
  timezone: string;
  from: string;
  to: string;
  /** 工具条左边：日期区间 */
  toolbar?: ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const [openId, setOpenId] = useState<number | null>(null);

  const summary = useQuery({
    queryKey: ['attendance-summary', siteId, from, to],
    // 区间必须按工地时区换算（见 lib/site-time.ts 顶部那段踩坑记录）
    queryFn: () => attendanceApi.summary(siteId, siteDayRange(from, to, timezone)),
  });

  const people = summary.data?.people ?? [];
  const totals = summary.data?.totals;
  const maxShiftMinutes = summary.data?.maxShiftMinutes ?? 960;
  const opened = people.find((p) => p.user.id === openId) ?? null;
  const dates = listDates(from, to);
  const matrix = dates.length <= MATRIX_MAX_DAYS;

  // 汇总说的始终是**整个区间**的数字（那是管理员真正要的那个数）
  const summaryLine = totals && totals.people > 0 && (
    <span className="flex flex-wrap items-center gap-x-4">
      <span>{t('attendance.totalPeople', { count: totals.people })}</span>
      <span>
        {t('attendance.totalHours')} <b><Duration minutes={totals.workedMinutes} /></b>
      </span>
      {totals.pendingCount > 0 && (
        <span className="text-safety-600">{t('attendance.pendingCount', { count: totals.pendingCount })}</span>
      )}
      {totals.anomalyCount > 0 && (
        <span className="text-danger">{t('attendance.anomalyCount', { count: totals.anomalyCount })}</span>
      )}
    </span>
  );

  return (
    <>
      <FilterBar summary={summaryLine}>{toolbar}</FilterBar>

      {summary.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {summary.isError && <Alert>{toMessage(summary.error)}</Alert>}
      {summary.data && people.length === 0 && <EmptyState title={t('attendance.empty')} />}

      {people.length > 0 && (
        <>
          {/* 窄屏：一人一行（CR-036） */}
          <ListGroup className="xl:hidden">
            {people.map((person) => (
              <ListRow
                key={person.user.id}
                onClick={() => setOpenId(person.user.id)}
                title={
                  <span className="inline-flex items-center gap-2">
                    {person.user.name}
                    <PersonBadges person={person} />
                  </span>
                }
                subtitle={`${t('attendance.workedDays')} ${person.workedDays}`}
                trailing={
                  <b className="text-body font-semibold tabular-nums text-ink">
                    <Duration minutes={person.workedMinutes} />
                  </b>
                }
              />
            ))}
          </ListGroup>

          {matrix ? (
            <DataTable
              minWidth={`${8 + dates.length * 3.5 + 5}rem`}
              columns={[
                { key: 'name', label: t('user.name'), className: 'w-32' },
                ...dates.map((date) => ({
                  key: date,
                  label: <DateHead date={date} language={i18n.language} />,
                  align: 'right' as const,
                })),
                { key: 'total', label: t('attendance.totalHours'), align: 'right' as const, className: 'w-20' },
              ]}
            >
              {people.map((person) => (
                <MatrixRow
                  key={person.user.id}
                  person={person}
                  dates={dates}
                  selected={openId === person.user.id}
                  onOpen={() => setOpenId(person.user.id)}
                />
              ))}
              {/* 每天合计：各人当天分钟数相加（都是服务端给的） */}
              <tr className="[&>td]:bg-surface-2/70 [&>td]:font-semibold">
                <td className="whitespace-nowrap text-ink">{t('purchases.colTotal')}</td>
                {dates.map((date) => {
                  const minutes = people.reduce((sum, p) => sum + (p.days.find((d) => d.date === date)?.minutes ?? 0), 0);
                  return (
                    <td key={date} className={cn('whitespace-nowrap text-right tabular-nums', minutes === 0 ? 'text-ink-4' : 'text-ink')}>
                      {minutes === 0 ? '—' : hours(minutes)}
                    </td>
                  );
                })}
                <td className="whitespace-nowrap text-right tabular-nums text-ink">{totals && hours(totals.workedMinutes)}</td>
              </tr>
            </DataTable>
          ) : (
            <DataTable
              minWidth="44rem"
              columns={[
                { key: 'name', label: t('user.name') },
                { key: 'employeeNo', label: t('user.employeeNo'), className: 'w-24' },
                { key: 'days', label: t('attendance.workedDays'), align: 'right', className: 'w-28' },
                { key: 'hours', label: t('attendance.totalHours'), align: 'right', className: 'w-36' },
                { key: 'todo', label: t('attendance.todoColumn'), className: 'w-60' },
              ]}
            >
              {people.map((person) => (
                <TableRow key={person.user.id} selected={openId === person.user.id} onOpen={() => setOpenId(person.user.id)}>
                  <td className="truncate font-medium text-ink">{person.user.name}</td>
                  <td className="whitespace-nowrap text-caption tabular-nums text-ink-3">{person.user.employeeNo}</td>
                  <td className="whitespace-nowrap text-right tabular-nums">{person.workedDays}</td>
                  <td className="whitespace-nowrap text-right tabular-nums">
                    <Duration minutes={person.workedMinutes} />
                  </td>
                  <td className="whitespace-nowrap">
                    <span className="flex items-center gap-1.5">
                      <PersonBadges person={person} />
                    </span>
                  </td>
                </TableRow>
              ))}
            </DataTable>
          )}
        </>
      )}

      <Drawer
        open={opened !== null}
        onClose={() => setOpenId(null)}
        size="wide"
        title={opened?.user.name ?? ''}
        subtitle={
          opened && (
            <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="tabular-nums">{opened.user.employeeNo}</span>
              <span>
                {t('attendance.workedDays')} {opened.workedDays}
              </span>
              <span>
                {t('attendance.totalHours')} <Duration minutes={opened.workedMinutes} />
              </span>
              <PersonBadges person={opened} />
            </span>
          )
        }
      >
        {opened && (
          <div className="-mx-5 -my-5 max-md:-mx-4 max-md:-my-4">
            <PersonDetail
              userId={opened.user.id}
              siteId={siteId}
              timezone={timezone}
              from={from}
              to={to}
              maxShiftMinutes={maxShiftMinutes}
              layout="cards"
            />
          </div>
        )}
      </Drawer>
    </>
  );
}

/** 周表的一行：一格一天。红 = 那天有没处理的异常，橙 = 有待复核的卡（不计入工时） */
function MatrixRow({
  person,
  dates,
  selected,
  onOpen,
}: {
  person: AttendancePerson;
  dates: string[];
  selected: boolean;
  onOpen: () => void;
}) {
  const { t } = useTranslation();
  const minutesOn = new Map(person.days.map((d) => [d.date, d.minutes]));
  const anomaly = new Set(person.anomalyDates ?? []);
  const pending = new Set(person.pendingDates ?? []);
  return (
    <TableRow selected={selected} onOpen={onOpen}>
      <td className="truncate">
        <span className="font-medium text-ink">{person.user.name}</span>
        {person.onShift && <span className="ml-1.5 text-caption text-ok">{t('attendance.onShift')}</span>}
      </td>
      {dates.map((date) => {
        const minutes = minutesOn.get(date) ?? 0;
        const bad = anomaly.has(date);
        const waiting = pending.has(date);
        return (
          <td
            key={date}
            className={cn(
              'whitespace-nowrap text-right tabular-nums',
              bad ? 'font-semibold text-danger' : waiting ? 'font-semibold text-safety-600' : minutes === 0 ? 'text-ink-4' : 'text-ink',
            )}
          >
            {minutes > 0 ? hours(minutes) : bad ? t('attendance.cellMissing') : waiting ? t('attendance.cellPending') : '—'}
          </td>
        );
      })}
      <td className="whitespace-nowrap text-right font-semibold tabular-nums text-ink">{hours(person.workedMinutes)}</td>
    </TableRow>
  );
}

/** 表头：「9/14」下面一行「一」。日期型的值按 UTC 渲染（代码约定） */
function DateHead({ date, language }: { date: string; language: string }) {
  const d = new Date(`${date}T00:00:00Z`);
  const weekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
  return (
    <span className={cn('inline-flex flex-col items-end leading-tight', weekend && 'text-ink-4')}>
      <span className="tabular-nums">{d.toLocaleDateString(language, { timeZone: 'UTC', month: 'numeric', day: 'numeric' })}</span>
      <span className="text-micro">{d.toLocaleDateString(language, { timeZone: 'UTC', weekday: 'narrow' })}</span>
    </span>
  );
}

/** 区间里的每一天，旧的在前 */
function listDates(from: string, to: string): string[] {
  const out: string[] = [];
  for (let d = new Date(`${from}T00:00:00Z`); d <= new Date(`${to}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1)) {
    out.push(d.toISOString().slice(0, 10));
    if (out.length > 400) break;
  }
  return out;
}

/** 周表格子里的工时：小时，一位小数 */
function hours(minutes: number): string {
  return (minutes / 60).toFixed(1);
}

/** 这个人的状态徽章：在岗、待复核、异常。表格行和手机列表共用 */
function PersonBadges({ person }: { person: AttendancePerson }) {
  const { t } = useTranslation();
  return (
    <>
      {/* 在岗不是异常（CR-052）：上班卡还在最长班次之内 */}
      {person.onShift && <Badge tone="ok">{t('attendance.onShift')}</Badge>}
      {person.pendingCount > 0 && (
        <Badge tone="warn">{t('attendance.pendingCount', { count: person.pendingCount })}</Badge>
      )}
      {person.anomalyCount > 0 && (
        <Badge tone="danger">{t('attendance.anomalyCount', { count: person.anomalyCount })}</Badge>
      )}
    </>
  );
}

/** 日期（加粗）+ 周几 */
function DayLabel({ date }: { date: string }) {
  const { i18n } = useTranslation();
  return (
    <span className="font-mono text-title font-bold tabular-nums text-ink">
      {new Date(`${date}T12:00:00Z`).toLocaleDateString(i18n.language, {
        timeZone: 'UTC',
        month: 'numeric',
        day: 'numeric',
        weekday: 'short',
      })}
    </span>
  );
}

/** 当天合计：只算已通过的段。一段都没配上写「—」—— 不写「0 小时 0 分」，那看着像来了没干活 */
function DayTotal({ day }: { day: DayRow }) {
  return (
    <span className="font-mono text-body font-medium tabular-nums text-ink">
      {day.minutes > 0 ? <Duration minutes={day.minutes} /> : '—'}
    </span>
  );
}

/** 这天的标记：待复核 · 未计入、没处理的异常、已处理的异常（带说明和撤销） */
function DayMarks({ day }: { day: DayRow }) {
  const { t } = useTranslation();
  const marks = day.sessions.flatMap((session) => {
    if (session.status === 'pending') {
      return [
        <Badge key={`${session.key}-pending`} tone="warn">
          {t('attendance.pendingNotCounted')}
        </Badge>,
      ];
    }
    if (!session.anomaly) return [];
    return [
      session.anomaly.resolution ? (
        <ResolvedAnomaly
          key={`${session.key}-anomaly`}
          punchId={session.anomaly.punchId}
          reason={session.anomaly.reason}
          resolution={session.anomaly.resolution}
        />
      ) : (
        <Badge key={`${session.key}-anomaly`} tone="danger">
          {t(`attendance.anomaly.${session.anomaly.reason}`)}
        </Badge>
      ),
    ];
  });
  if (marks.length === 0) return null;
  return <span className="flex flex-wrap items-center gap-1.5">{marks}</span>;
}

/**
 * 已经标记处理过的异常（CR-052）：灰徽章 + 谁、写了什么，能复核的人可以撤销。
 * 撤销之后它回到「异常」页签。
 */
function ResolvedAnomaly({
  punchId,
  reason,
  resolution,
}: {
  punchId: string;
  reason: UnpairedReason;
  resolution: NonNullable<Punch['anomalyResolution']>;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const canManage = usePermissions().can(Permission.ATTENDANCE_MANAGE);
  const [error, setError] = useState<string | null>(null);

  const undo = useMutation({
    mutationFn: () => attendanceApi.unresolveAnomaly(punchId),
    onSuccess: () => invalidateAttendance(queryClient),
    onError: (err) => setError(toMessage(err)),
  });

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone="neutral">{t('attendance.resolvedBadge', { reason: t(`attendance.anomaly.${reason}`) })}</Badge>
      <span className="text-caption text-ink-2">
        {t('attendance.resolvedNote', { name: resolution.resolvedByName ?? '—', note: resolution.note })}
      </span>
      {canManage && (
        <Button variant="ghost" loading={undo.isPending} onClick={() => undo.mutate()}>
          {t('attendance.unresolve')}
        </Button>
      )}
      {error && <span className="text-caption text-danger">{error}</span>}
    </span>
  );
}

/**
 * 展开后的明细：**一天一行，每段上班 ─── 下班连成一条线**，右边是当天合计和标记。
 *
 * 客户原话「点开每个人也是一眼就看出每天的上班打卡下班，和每天时间」
 * 「同一天分段打卡上下班两个时间最好用一些把两个时间连接对应下」。
 * 原来那张一条条打卡的表（距离、来源、原因、复核按钮）收进每天末尾的「打卡明细」。
 *
 * 只在展开时才请求这个人的打卡 —— 一次把所有人的明细都拉下来，
 * 在两周 × 二十人的规模上就是几百条谁也不会看的数据。
 * `xl` 以上是表格，窄屏（含 App）是一天一张小卡（CR-036 / CR-047）。
 */
function PersonDetail({
  userId,
  siteId,
  timezone,
  from,
  to,
  maxShiftMinutes,
  layout = 'table',
}: {
  userId: number;
  siteId: number;
  timezone: string;
  from: string;
  to: string;
  maxShiftMinutes: number;
  layout?: 'table' | 'cards';
}) {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const [openDay, setOpenDay] = useState<string | null>(null);

  const punches = useQuery({
    queryKey: ['punches', siteId, userId, from, to, maxShiftMinutes],
    // 前后各多取一个最长班次（CR-066）：区间边界上的夜班，另一张卡在区间外面
    queryFn: () =>
      attendanceApi.list(siteId, { ...widenRange(siteDayRange(from, to, timezone), maxShiftMinutes), userId }),
  });

  if (punches.isLoading) {
    return (
      <div className="flex justify-center border-t border-rule py-8 text-ink-3">
        <Spinner />
      </div>
    );
  }
  if (punches.isError) {
    return (
      <div className="border-t border-rule p-4">
        <Alert>{toMessage(punches.error)}</Alert>
      </div>
    );
  }

  const days = buildDays(punches.data ?? [], timezone, maxShiftMinutes, new Date(), { from, to });
  const toggleDay = (date: string) => setOpenDay((current) => (current === date ? null : date));

  const sessions = (day: DayRow) => (
    <div className="flex max-w-xl flex-col gap-1.5">
      {day.sessions.map((session) => (
        <SessionLine
          key={session.key}
          timezone={timezone}
          status={session.status}
          start={session.start?.occurredAt ?? null}
          end={session.end?.occurredAt ?? null}
          minutes={session.minutes}
          endsNextDay={session.endsNextDay}
        />
      ))}
    </div>
  );

  const detailsToggle = (day: DayRow) => (
    <Button variant="ghost" onClick={() => toggleDay(day.date)} aria-expanded={openDay === day.date}>
      {t('attendance.punchDetails')}
    </Button>
  );

  if (layout === 'cards') {
    return (
      <div className="flex flex-col gap-2 border-t border-rule bg-surface-2/40 p-3">
        {days.map((day) => (
          <div key={day.date} className="rounded-md border border-rule bg-surface px-3 py-2.5">
            <div className="flex items-baseline justify-between gap-2">
              <DayLabel date={day.date} />
              <DayTotal day={day} />
            </div>
            <div className="mt-2">{sessions(day)}</div>
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <DayMarks day={day} />
              <span className="ml-auto">{detailsToggle(day)}</span>
            </div>
            {openDay === day.date && (
              <ul className="mt-2 overflow-hidden rounded-md border border-rule">
                {day.punches.map((punch) => (
                  <PunchItem key={punch.id} punch={punch} timezone={timezone} />
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="border-t border-rule bg-surface-2/40">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] table-fixed text-body">
          <colgroup>
            <col className="w-32" />
            <col />
            <col className="w-32" />
            <col className="w-80" />
          </colgroup>

          <thead>
            <tr className="border-b border-rule text-caption uppercase tracking-wide text-ink-3">
              <th className="px-3 py-2 text-left font-medium">{t('attendance.colDay')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('attendance.colSessions')}</th>
              <th className="px-3 py-2 text-right font-medium">{t('attendance.colDayTotal')}</th>
              <th className="px-3 py-2 text-left font-medium">{t('attendance.colMarks')}</th>
            </tr>
          </thead>

          <tbody>
            {days.map((day) => (
              <Fragment key={day.date}>
                <tr className="border-b border-rule align-top">
                  <td className="px-3 py-3">
                    <DayLabel date={day.date} />
                  </td>
                  <td className="px-3 py-3">{sessions(day)}</td>
                  <td className="px-3 py-3 text-right">
                    <DayTotal day={day} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-col items-start gap-1">
                      <DayMarks day={day} />
                      {detailsToggle(day)}
                    </div>
                  </td>
                </tr>

                {openDay === day.date && (
                  <tr className="border-b border-rule">
                    <td colSpan={4} className="px-3 pb-3">
                      {/* 原来的逐条打卡表：一张表、列宽 colgroup 定死（CR-031） */}
                      <table className="w-full table-fixed text-body">
                        <colgroup>
                          <col className="w-24" />
                          <col className="w-28" />
                          <col className="w-28" />
                          <col className="w-24" />
                          <col />
                          <col className="w-24" />
                          <col className="w-40" />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-rule text-caption uppercase tracking-wide text-ink-3">
                            <th className="px-3 py-2 text-left font-medium">{t('attendance.colTime')}</th>
                            <th className="px-3 py-2 text-left font-medium">{t('attendance.colType')}</th>
                            <th className="px-3 py-2 text-right font-medium">{t('attendance.colDistance')}</th>
                            <th className="px-3 py-2 text-left font-medium">{t('attendance.colSource')}</th>
                            <th className="px-3 py-2 text-left font-medium">{t('attendance.colReason')}</th>
                            <th className="px-3 py-2 text-left font-medium">{t('attendance.colStatus')}</th>
                            <th className="px-3 py-2 text-right font-medium">{t('attendance.colAction')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {day.punches.map((punch) => (
                            <PunchRow key={punch.id} punch={punch} timezone={timezone} />
                          ))}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** 「8 小时 36 分」。小数形式留给导出的表格（见 lib/format.ts） */
function Duration({ minutes }: { minutes: number }) {
  const { t } = useTranslation();
  const { hours, minutes: mins } = splitHours(minutes);
  return <>{t('attendance.duration', { hours, minutes: mins })}</>;
}
